import React, { useEffect } from 'react';
import useThemeStore from '../store/themeStore';

const Maintenance: React.FC = () => {
  const theme = useThemeStore((state) => state.theme);
  
  // Get theme colors or use defaults
  const color1 = theme?.color1;
  const color2 = theme?.color2;

  useEffect(() => {
    // Create floating particles
    const createParticles = () => {
      const particleCount = 50;
      const maintenancePage = document.querySelector('.maintenance-page');
      if (!maintenancePage) return;
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 6 + 's';
        particle.style.animationDuration = (Math.random() * 3 + 3) + 's';
        maintenancePage.appendChild(particle);
      }
    };

    // Interactive orb follows mouse
    const orb = document.querySelector('.interactive-orb') as HTMLElement;
    const handleMouseMove = (e: MouseEvent) => {
      if (orb) {
        const x = e.clientX;
        const y = e.clientY;
        
        orb.style.left = (x - 100) + 'px';
        orb.style.top = (y - 100) + 'px';
      }
    };

    // Add click effect to glass card
    const glassCard = document.querySelector('.glass-card') as HTMLElement;
    const handleCardClick = () => {
      if (glassCard) {
        glassCard.style.transform = 'scale(0.95)';
        setTimeout(() => {
          glassCard.style.transform = '';
        }, 150);
      }
    };

    // Dynamic status messages
    const statusMessages = [
      'Upgrading systems...',
      'Optimizing performance...',
      'Installing new features...',
      'Almost ready...',
      'Finalizing updates...'
    ];
    
    const statusText = document.querySelector('.status-text') as HTMLElement;
    let messageIndex = 0;
    
    const statusInterval = setInterval(() => {
      if (statusText) {
        statusText.style.opacity = '0';
        setTimeout(() => {
          messageIndex = (messageIndex + 1) % statusMessages.length;
          statusText.textContent = statusMessages[messageIndex];
          statusText.style.opacity = '1';
        }, 300);
      }
    }, 3000);

    // Add subtle 3D tilt effect
    const handleCardMouseMove = (e: MouseEvent) => {
      if (glassCard) {
        const rect = glassCard.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 10;
        const rotateY = (centerX - x) / 10;
        
        glassCard.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      }
    };
    
    const handleCardMouseLeave = () => {
      if (glassCard) {
        glassCard.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
      }
    };

    // Initialize
    createParticles();
    
    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    if (glassCard) {
      glassCard.addEventListener('click', handleCardClick);
      glassCard.addEventListener('mousemove', handleCardMouseMove);
      glassCard.addEventListener('mouseleave', handleCardMouseLeave);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (glassCard) {
        glassCard.removeEventListener('click', handleCardClick);
        glassCard.removeEventListener('mousemove', handleCardMouseMove);
        glassCard.removeEventListener('mouseleave', handleCardMouseLeave);
      }
      clearInterval(statusInterval);
      
      // Remove particles
      const particles = document.querySelectorAll('.particle');
      particles.forEach(particle => particle.remove());
    };
  }, []);

  return (
    <div className="maintenance-page">
      <style>{`
        /* Reset default margins and padding */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          margin: 0;
          padding: 0;
          height: 100%;
          overflow-x: hidden;
        }

        .maintenance-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          overflow: hidden;
          background: linear-gradient(135deg, ${color1} 0%, ${color2} 100%);
          min-height: 100vh;
          height: 100vh;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .maintenance-page * {
          box-sizing: border-box;
        }

        .animated-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, ${color1} 0%, ${color2} 50%, ${color1} 100%);
          background-size: 400% 400%;
          animation: gradientShift 8s ease infinite;
          z-index: 1;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: float 6s ease-in-out infinite;
          bottom: 0px;
          z-index: 2;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          50% { transform: translateY(-100px) rotate(180deg); }
        }

        .container {
          position: relative;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          text-align: center;
          margin: 0;
          box-sizing: border-box;
          max-width: 600px;
          width: 100%;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 3rem 2rem;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
          animation: cardFloat 3s ease-in-out infinite;
          transition: all 0.3s ease;
          margin: 0 auto;
          display: block;
        }

        .glass-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 35px 70px rgba(0, 0, 0, 0.2);
        }

        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .gear-container {
          position: relative;
          margin-bottom: 2rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .gear {
          width: 80px;
          height: 80px;
          border: 6px solid rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          position: relative;
          animation: spin 4s linear infinite;
        }

        .gear::before {
          content: '';
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 15px solid rgba(255, 255, 255, 0.8);
        }

        .gear::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 20px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .gear-small {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.6);
          position: absolute;
          animation: spin 2s linear infinite reverse;
        }

        .gear-small::after {
          width: 10px;
          height: 10px;
        }

        .gear-1 {
          top: -20px;
          right: -30px;
        }

        .gear-2 {
          bottom: -20px;
          left: -30px;
        }

        .maintenance-page h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: white;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
          animation: titleGlow 2s ease-in-out infinite alternate;
        }

        @keyframes titleGlow {
          from { text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3); }
          to { text-shadow: 0 2px 20px rgba(255, 255, 255, 0.3); }
        }

        .maintenance-page p {
          font-size: 1.1rem;
          margin-bottom: 2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.6;
        }

        .progress-container {
          width: 100%;
          height: 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 1rem;
        }

        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, ${color1 || '#4f46e5'}, ${color2 || '#7c3aed'});
          border-radius: 10px;
          animation: progress 3s ease-in-out infinite;
          position: relative;
          width: 0%; /* Ensure initial state */
        }

        .progress-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          animation: shimmer 1.5s ease-in-out infinite;
        }

        @keyframes progress {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .status-text {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 2rem;
          transition: opacity 0.3s ease;
        }

        .cta-section {
          margin-bottom: 4rem;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 15px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }

        .cta-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .cta-subtitle {
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.85);
          margin-bottom: 1.5rem;
          line-height: 1.4;
        }

        .cta-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: ${color1 || '#4f46e5'};
          backdrop-filter: blur(20px);
          border: 3px solid #ffffff;
          color: white;
          padding: 1.2rem 2.5rem;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          box-shadow: 
            0 0 0 3px rgba(255, 255, 255, 0.3),
            0 15px 35px rgba(0, 0, 0, 0.2);
          animation: borderPulse 2s ease-in-out infinite;
          position: relative;
          cursor: pointer;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
        }

        .cta-button:hover {
          background: ${color1 ? `color-mix(in srgb, ${color1} 80%, white 20%)` : '#6366f1'};
          transform: translateY(-3px) scale(1.05);
          box-shadow: 
            0 0 0 4px rgba(255, 255, 255, 0.5),
            0 20px 45px rgba(0, 0, 0, 0.3);
          animation: none;
        }

        @keyframes borderPulse {
          0%, 100% { 
            box-shadow: 
              0 0 0 3px rgba(255, 255, 255, 0.3),
              0 15px 35px rgba(0, 0, 0, 0.2);
          }
          50% { 
            box-shadow: 
              0 0 0 5px rgba(255, 255, 255, 0.6),
              0 20px 45px rgba(0, 0, 0, 0.3);
          }
        }

        .interactive-orb {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.1), transparent);
          pointer-events: none;
          transition: all 0.3s ease;
          z-index: 3;
        }

        .click-here-text {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.7);
          text-align: center;
          margin-top: 0.5rem;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .container {
            padding: 1rem;
          }
          
          .glass-card {
            padding: 2rem 1.5rem;
            margin: 0;
          }
          
          .maintenance-page h1 {
            font-size: 2rem;
          }
          
          .gear {
            width: 60px;
            height: 60px;
          }
          
          .gear-small {
            width: 30px;
            height: 30px;
            border: 2px solid rgba(255, 255, 255, 0.6);
          }

          .cta-title {
            font-size: 1.1rem;
          }

          .cta-button {
            padding: 0.9rem 1.5rem;
            font-size: 0.9rem;
          }
        }
      `}</style>
      
      <div className="animated-bg"></div>
      <div className="interactive-orb"></div>
      
      <div className="container">
        <div className="glass-card">
          <div className="cta-section">
            <div className="cta-title">Don't Stop Your Property Search!</div>
            <div className="cta-subtitle">
              While we upgrade this site, continue discovering amazing properties on our live marketplace
            </div>
            <a 
              href="https://mypropertymarketplace.co.uk/listing" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cta-button"
            >
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              Browse Properties Now
            </a>
          </div>
          
          <div className="gear-container">
            <div className="gear">
              <div className="gear gear-small gear-1"></div>
              <div className="gear gear-small gear-2"></div>
            </div>
          </div>
          
          <h1>Under Maintenance</h1>
          <p>We're working hard to improve your experience. Our site will be back online shortly with exciting new features!</p>
          
          <div className="progress-container">
            <div className="progress-bar"></div>
          </div>
          
          <div className="status-text">Upgrading systems...</div>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;